'use client';

import { usePathname } from 'next/navigation';
import { useLayoutEffect, useRef, useState } from 'react';
import ActiveAnnouncementBanner from '@/components/layout/ActiveAnnouncementBanner';
import Footer from '@/components/layout/Footer';
import Navbar from '@/components/layout/Navbar';
import OverlayRoot from '@/components/layout/OverlayRoot';
import Providers from '@/components/Providers';
import FloatingChatButton from '@/components/ui/FloatingChatButton';
import {
    isSensitiveReplayPath,
    SENSITIVE_REPLAY_BLOCK_CLASS,
} from '@/lib/analytics-privacy.mjs';
import { getAppShellConfig } from '@/lib/app-shell-paths';

export default function AppShellWrapper({ children, productData }) {
    const [announcementOffset, setAnnouncementOffset] = useState(0);
    const pathname = usePathname();
    const previousPathRef = useRef(pathname);
    const shellConfig = getAppShellConfig(pathname);
    // Hook for the Umami recorder Block Selector (dashboard must set it to
    // `.umami-block`). Drops this subtree from recordings even when the
    // recorder is already running — e.g. client-side navigation from a
    // public page into an inquiry/admin route.
    const shellClassName = `flex min-h-screen flex-col${isSensitiveReplayPath(pathname) ? ` ${SENSITIVE_REPLAY_BLOCK_CLASS}` : ''}`;

    useLayoutEffect(() => {
        if (previousPathRef.current !== pathname) {
            window.scrollTo(0, 0);
        }

        previousPathRef.current = pathname;
    }, [pathname]);

    return (
        <Providers productData={productData}>
            <div
                className={shellClassName}
                style={{
                    boxSizing: 'border-box',
                    paddingTop: shellConfig.showAnnouncement
                        ? `${announcementOffset}px`
                        : '0px',
                    '--announcement-offset': shellConfig.showAnnouncement
                        ? `${announcementOffset}px`
                        : '0px',
                }}
            >
                {shellConfig.showAnnouncement ? (
                    <ActiveAnnouncementBanner
                        onHeightChange={setAnnouncementOffset}
                    />
                ) : null}
                {shellConfig.showNavbar ? <Navbar /> : null}
                <div className="flex flex-1 flex-col">
                    <main
                        id="main-content"
                        className="flex-1"
                        aria-label="Main content"
                    >
                        {children}
                    </main>
                    {shellConfig.showFooter ? <Footer /> : null}
                </div>
            </div>
            {shellConfig.showFloatingChat ? <FloatingChatButton /> : null}
            <OverlayRoot />
        </Providers>
    );
}
